/**
 * O casco do app.
 *
 * Fase 0: a navegação existe e o marcador funciona, mas nenhuma rota de
 * domínio foi construída. Os destinos estão aqui porque a agrupação deles é
 * uma decisão de produto, não de layout — agrupar por ENTIDADE do domínio é
 * justamente o que separa isto das 179 telas do concorrente.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { VERSAO_CONTRATO } from '@pauta/contract'

import type { Ajuda } from '../api'
import { Icon, Stave, type IconName } from './Icon'
import { ThemeToggle } from './ThemeToggle'

/**
 * Um destino do menu — e o manual dele.
 *
 * **`ajuda` é obrigatória de propósito.** Destino novo sem manual não compila,
 * e é a única forma de a documentação acompanhar o código sem depender de
 * alguém lembrar. Para os recursos de cadastro, ela vem do catálogo da API;
 * para as telas de tarefa, está declarada em `App.tsx`, ao lado do destino.
 */
export type Destino = { id: string; rotulo: string; icone: IconName; ajuda: Ajuda }
export type Grupo = { rotulo: string; itens: Destino[] }

const CHAVE_ABERTOS = 'pauta:menu-abertos'

/** Nulo = nunca escolheu: abre só o grupo da tela atual. */
function lerAbertos(): Set<string> | null {
  try {
    const salvo = localStorage.getItem(CHAVE_ABERTOS)
    if (salvo) {
      const lista: unknown = JSON.parse(salvo)
      if (Array.isArray(lista)) return new Set(lista.map(String))
    }
  } catch {
    // Sem localStorage ou valor estragado: segue no padrão.
  }
  return null
}

function gravarAbertos(abertos: Set<string>) {
  try {
    localStorage.setItem(CHAVE_ABERTOS, JSON.stringify([...abertos]))
  } catch {
    // A escolha não persiste; a sessão atual continua certa.
  }
}

export function AppShell({
  grupos,
  atual,
  onIr,
  offline,
  usuario,
  podeTrocarProjeto,
  aoTrocarProjeto,
  aoAbrirEscola,
  aoSair,
  ocultos,
  projeto,
  children,
}: {
  grupos: Grupo[]
  atual: string
  onIr: (id: string) => void
  offline?: boolean
  usuario?: { nome: string; email: string }
  podeTrocarProjeto?: boolean
  aoTrocarProjeto?: () => void
  /** Abrir a tela de gerenciar a escola. O nome na barra é o caminho até ela. */
  aoAbrirEscola?: () => void
  aoSair?: () => void
  /** Quantos itens o projeto escondeu. Zero não mostra o rodapé. */
  ocultos?: number
  /** A escola aberta. Sem ela o cabeçalho não inventa um nome. */
  projeto?: { nome: string; ano_letivo: number }
  children: React.ReactNode
}) {
  const navRef = useRef<HTMLElement>(null)
  const [marcador, setMarcador] = useState<{ y: number; h: number } | null>(null)

  /**
   * Os grupos abertos do menu.
   *
   * O menu passou de vinte itens em quatro grupos e deixou de caber numa
   * tela. Cada grupo recolhe; sem escolha salva, abre só o grupo da tela
   * atual. A escolha vai para localStorage, num try/catch como o tema.
   */
  const [abertos, setAbertos] = useState<Set<string> | null>(lerAbertos)
  const grupoAtual = grupos.find((g) => g.itens.some((i) => i.id === atual))?.rotulo
  const estaAberto = (rotulo: string) => (abertos ? abertos.has(rotulo) : rotulo === grupoAtual)

  const alternarGrupo = (rotulo: string) => {
    const base = abertos ?? new Set(grupoAtual ? [grupoAtual] : [])
    const novo = new Set(base)
    if (novo.has(rotulo)) novo.delete(rotulo)
    else novo.add(rotulo)
    setAbertos(novo)
    gravarAbertos(novo)
  }

  // Chegar a uma tela de um grupo fechado — pelo manual, pelo tour, pelo
  // nome da escola — abre o grupo dela. Só ao navegar: quem fecha o grupo da
  // tela em que está quis fechá-lo, e reabrir na mesma hora seria brigar.
  useEffect(() => {
    if (!grupoAtual) return
    setAbertos((atuais) => {
      if (!atuais || atuais.has(grupoAtual)) return atuais
      const novo = new Set(atuais).add(grupoAtual)
      gravarAbertos(novo)
      return novo
    })
  }, [grupoAtual, atual])

  /**
   * O marcador da pauta. Mede o item ativo e desliza até ele.
   *
   * Medição em vez de posição declarada porque os itens têm alturas iguais
   * hoje e não terão quando houver contagem em duas linhas.
   */
  const medir = useCallback(() => {
    const nav = navRef.current
    if (!nav) return
    const ativo = nav.querySelector<HTMLElement>('[aria-current="page"]')
    // Num grupo recolhido o item existe mas não tem caixa: sem marcador.
    if (!ativo || ativo.offsetParent === null) {
      setMarcador(null)
      return
    }
    const base = nav.getBoundingClientRect()
    const alvo = ativo.getBoundingClientRect()
    setMarcador({ y: alvo.top - base.top, h: alvo.height })
  }, [])

  useLayoutEffect(medir, [medir, atual, abertos])

  useEffect(() => {
    const nav = navRef.current
    if (!nav) return
    const observador = new ResizeObserver(medir)
    observador.observe(nav)
    return () => observador.disconnect()
  }, [medir])

  return (
    <div className="shell">
      <a className="skip-link" href="#conteudo">
        Pular para o conteúdo
      </a>

      <aside className="rail no-print">
        <div className="wordmark">
          <Stave />
          <span className="wordmark__name">Pauta</span>
          <span className="wordmark__year">2027</span>
        </div>

        <nav className="nav" ref={navRef} aria-label="Seções">
          {marcador && (
            <span
              className="nav__marker"
              style={{ transform: `translateY(${marcador.y}px)`, height: marcador.h }}
            />
          )}
          {grupos.map((grupo) => {
            const aberto = estaAberto(grupo.rotulo)
            const idLista = `nav-grupo-${grupo.rotulo.replace(/\W+/g, '-')}`
            return (
            <div className="nav__group" key={grupo.rotulo}>
              <h2 className="nav__label">
                <button
                  type="button"
                  className="nav__toggle"
                  aria-expanded={aberto}
                  aria-controls={idLista}
                  onClick={() => alternarGrupo(grupo.rotulo)}
                >
                  <span>{grupo.rotulo}</span>
                  {!aberto && <span className="nav__toggle-n">{grupo.itens.length}</span>}
                  <span className="nav__chevron" aria-hidden>
                    <Icon name="recolher" size={14} />
                  </span>
                </button>
              </h2>
              <ul className="nav__list" id={idLista} hidden={!aberto}>
                {grupo.itens.map((item) => (
                  <li className="nav__item" key={item.id}>
                    <button
                      type="button"
                      className="nav__link"
                      aria-current={atual === item.id ? 'page' : undefined}
                      onClick={() => onIr(item.id)}
                    >
                      <span className="nav__icon">
                        <Icon name={item.icone} />
                      </span>
                      {item.rotulo}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            )
          })}
        </nav>

        {/* O rodapé do menu é discreto de propósito: ajustar a navegação é
            tarefa rara, e um item no corpo da lista disputaria atenção com as
            telas de trabalho todo dia. Só aparece quando há o que religar. */}
        {ocultos !== undefined && ocultos > 0 && (
          <button
            type="button"
            className="nav__ajuste"
            aria-current={atual === 'menu' ? 'page' : undefined}
            onClick={() => onIr('menu')}
          >
            <Icon name="ajustar" size={14} />
            {ocultos} {ocultos === 1 ? 'seção oculta' : 'seções ocultas'}
          </button>
        )}

        {/* A ajuda fica aqui, e não no corpo da lista, pelo mesmo motivo — mas
            ao contrário do ajuste ela está SEMPRE presente: quem precisa dela
            não sabe o nome da tela que procura, e um item que aparece só às
            vezes não se aprende onde fica.

            Clicar com uma tela aberta abre o manual já naquela tela. É o que
            transforma um manual numa ajuda de contexto sem precisar de duas
            implementações. */}
        <button
          type="button"
          className="nav__ajuste nav__ajuda"
          aria-current={atual === 'ajuda' ? 'page' : undefined}
          onClick={() => onIr('ajuda')}
        >
          <Icon name="ajuda" size={14} />
          Ajuda
        </button>
      </aside>

      <div className="work">
        <header className="topbar no-print">
          <div className="topbar__crumbs">
            {/* O nome vem do projeto aberto.
                Até aqui era "Colégio Dom Pedro II" escrito no código — e
                passou a ser defeito no dia em que o banco ganhou uma escola
                de verdade: um nome fictício sobre dado real é a pior
                combinação possível numa tela que alguém está avaliando. */}
            {/* **O nome é o caminho para gerenciar a escola.** Clicar no
                nome da organização para chegar às configurações dela é a
                convenção que a pessoa já traz de outras ferramentas, e aqui
                resolve um problema concreto: a tela precisava existir em
                algum lugar sempre visível, sem disputar espaço no menu com
                as telas de trabalho de todo dia. */}
            {aoAbrirEscola ? (
              <button
                type="button"
                className="topbar__project topbar__project--botao"
                onClick={aoAbrirEscola}
                aria-current={atual === 'escola' ? 'page' : undefined}
                title="Gerenciar esta escola"
              >
                {projeto?.nome ?? '—'}
              </button>
            ) : (
              <span className="topbar__project">{projeto?.nome ?? '—'}</span>
            )}
            {projeto && (
              <>
                <span className="topbar__sep" aria-hidden>
                  /
                </span>
                <span className="field__hint">{projeto.ano_letivo}</span>
              </>
            )}
            {offline && (
              <span className="chip" title="A API não respondeu; só a fundação está disponível.">
                API fora do ar
              </span>
            )}
          </div>
          <div className="topbar__actions">
            <span className="mono topbar__stat">
              <span className="sr-only">Contrato </span>v{VERSAO_CONTRATO}
            </span>
            {podeTrocarProjeto && (
              <button type="button" className="btn btn--quieto" onClick={aoTrocarProjeto}>
                Trocar de escola
              </button>
            )}
            {usuario && (
              // Quem está logado fica à vista. Numa ferramenta compartilhada
              // entre a secretaria e a coordenação, "de quem é esta sessão?"
              // não pode exigir um clique para ser respondido.
              <span className="topbar__usuario" title={usuario.email}>
                {usuario.nome || usuario.email}
              </span>
            )}
            {aoSair && (
              <button type="button" className="btn btn--quieto" onClick={aoSair}>
                Sair
              </button>
            )}
            <ThemeToggle />
          </div>
        </header>

        <main className="work__body" id="conteudo" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  )
}
